import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit2, Home, Search } from 'react-feather';
import config from '../../../../config';
import useEntityCrud from '../shared/useEntityCrud';
import KpiStrip from '../shared/KpiStrip';
import Pagination from '../shared/Pagination';

const PropertiesTab = ({ q, page, onPageChange, onEdit, onAdd, canManage, reloadKey }) => {
    const { t } = useTranslation();
    const concessionaireId = localStorage.getItem('concessionaireId');
    const propertyAccess = localStorage.getItem('propertyAccess');

    const { paged, total, pageCount, page: safePage, loading, rows } = useEntityCrud({
        endpoint: `${config.hostname}/property/getpropertybyconcessionaire`,
        payload: {
            property_concessionaire_id: concessionaireId,
            property_id: propertyAccess,
        },
        searchFields: [
            'property_name',
            'property_mall_code',
            'property_country_id.country_name',
        ],
        q,
        page,
        pageSize: 15,
        dependencies: [reloadKey],
    });

    const active = rows.filter((r) => !r.property_disable).length;
    const disabled = rows.length - active;

    const tiles = useMemo(() => ([
        { label: 'Total', value: rows.length, icon: <Home size={16} />, tone: 'accent' },
        { label: 'Active', value: active, tone: 'green' },
        { label: 'Disabled', value: disabled, tone: 'amber' },
    ]), [rows.length, active, disabled]);

    return (
        <>
            <KpiStrip tiles={tiles} />
            <div className="v3-card v3-sadmin-list-card">
                <div className="v3-sadmin-list-header">
                    <div className="v3-sadmin-list-title">
                        <h2 className="v3-title">{t('Properties')}</h2>
                        <span className="v3-sadmin-list-count">{total}</span>
                    </div>
                    <div className="v3-sadmin-list-actions">
                        {canManage && (
                            <button type="button" className="v3-btn v3-btn-primary" onClick={onAdd}>
                                + {t('Add property')}
                            </button>
                        )}
                    </div>
                </div>
                <div className="v3-sadmin-table-wrap">
                    {loading ? (
                        <div className="v3-sadmin-empty"><p>{t('Loading properties…')}</p></div>
                    ) : total === 0 ? (
                        <div className="v3-sadmin-empty">
                            <Search size={28} />
                            <h3>{t('No properties found')}</h3>
                            <p>{q ? t('Try clearing the search.') : t('Add your first property to get started.')}</p>
                        </div>
                    ) : (
                        <table className="v3-sadmin-table">
                            <thead>
                                <tr>
                                    <th>{t('Property')}</th>
                                    <th>{t('Country')}</th>
                                    <th>{t('Mall code')}</th>
                                    <th>{t('Stores')}</th>
                                    <th>{t('Status')}</th>
                                    {canManage && <th style={{ width: 80 }}>{t('Actions')}</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((row) => (
                                    <tr key={row._id} onClick={() => canManage && onEdit(row._id)}>
                                        <td className="v3-sadmin-cell-strong">{row.property_name}</td>
                                        <td className="v3-sadmin-cell-muted">
                                            {row.property_country_id?.country_name || '—'}
                                        </td>
                                        <td><span className="v3-sadmin-cell-code">{row.property_mall_code}</span></td>
                                        <td className="v3-sadmin-cell-muted">{row.property_stores ?? '—'}</td>
                                        <td>
                                            <span className={`v3-sadmin-pill ${row.property_disable ? 'off' : 'live'}`}>
                                                {row.property_disable ? t('Disabled') : t('Active')}
                                            </span>
                                        </td>
                                        {canManage && (
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="v3-sadmin-row-actions">
                                                    <button
                                                        type="button"
                                                        className="v3-sadmin-icon-btn"
                                                        onClick={() => onEdit(row._id)}
                                                        aria-label={t('Edit')}
                                                    >
                                                        <Edit2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <Pagination
                    page={safePage}
                    pageCount={pageCount}
                    total={total}
                    pageSize={15}
                    onPageChange={onPageChange}
                />
            </div>
        </>
    );
};

export default PropertiesTab;
